// Fleet data layer — 300+ vehicles across all branches.
// Deterministic: same vehicle id always yields the same attributes across reloads.

import { BRANCHES } from './orgData';

// ------------ Seeded PRNG ------------
const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (a) => () => {
  a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const rng = (seedStr) => mulberry32(hashSeed(seedStr));
const pick = (r, arr) => arr[Math.floor(r() * arr.length)];
const range = (r, lo, hi) => lo + r() * (hi - lo);
const intRange = (r, lo, hi) => Math.floor(range(r, lo, hi + 1));

// ------------ Catalogs ------------
export const VEHICLE_TYPES = [
  {
    id: 'fitment-truck',
    name: 'Fitment Truck',
    share: 0.30,
    models: ['Isuzu NPR 400', 'Hino 300 614', 'Mercedes Sprinter 519', 'Toyota Dyna 6-104'],
    baseKm: 3200, kmVar: 1100,
    fuelEff: 14.5, fuelEffVar: 2.2,
    baseMaint: 7800, maintVar: 3200,
    baseRev: 212000, revVar: 68000,
    baseUtil: 82, utilVar: 8,
    servKmInterval: 15000,
  },
  {
    id: 'service-van',
    name: 'Service Van',
    share: 0.42,
    models: ['Ford Transit Custom', 'VW Crafter 35', 'Hyundai H100', 'Nissan NV350', 'Toyota Quantum'],
    baseKm: 4100, kmVar: 1400,
    fuelEff: 10.8, fuelEffVar: 1.6,
    baseMaint: 5200, maintVar: 2200,
    baseRev: 168000, revVar: 54000,
    baseUtil: 86, utilVar: 7,
    servKmInterval: 20000,
  },
  {
    id: 'supervisor-bakkie',
    name: 'Supervisor Bakkie',
    share: 0.18,
    models: ['Toyota Hilux 2.4GD-6', 'Ford Ranger 2.0 SiT', 'Isuzu D-Max 250', 'Nissan Navara 2.5DDTi'],
    baseKm: 2600, kmVar: 900,
    fuelEff: 9.8, fuelEffVar: 1.4,
    baseMaint: 3800, maintVar: 1800,
    baseRev: 0, revVar: 0, // supervisors don't carry stock
    baseUtil: 72, utilVar: 10,
    servKmInterval: 15000,
  },
  {
    id: 'courier-bike',
    name: 'Courier Bike',
    share: 0.10,
    models: ['Honda CB125F', 'Yamaha Crux 110', 'Bajaj Boxer 150', 'Suzuki GD110'],
    baseKm: 1800, kmVar: 700,
    fuelEff: 2.6, fuelEffVar: 0.6,
    baseMaint: 900, maintVar: 500,
    baseRev: 34000, revVar: 14000,
    baseUtil: 68, utilVar: 12,
    servKmInterval: 5000,
  },
];

// Branch → fleet-size tier
const FLEET_TIER = {
  'Metro-Flagship': [22, 26],
  'Metro-Standard': [14, 18],
  'Regional-Town': [10, 12],
  'Africa-Metro': [12, 15],
};

const FUEL_PRICE_ZAR_PER_L = 22.4; // diesel blended
const TECH_FIRST = ['Thabo', 'Sipho', 'Lungelo', 'Mandla', 'Nhlanhla', 'Bongani', 'Jabulani', 'Karabo', 'Tshepo', 'Kagiso', 'Wandile', 'Sizwe', 'Andile', 'Pieter', 'Johan', 'Ruan', 'Dawid', 'Francois', 'Riaan', 'Hennie', 'Ashwin', 'Pravin', 'Deshen', 'Yusuf', 'Imran', 'Faizel', 'Sibusiso', 'Themba', 'Lwazi', 'Khaya', 'Mpho', 'Tebogo', 'Lebo', 'Vusi', 'Zola', 'Joseph', 'Daniel', 'Samuel'];
const TECH_LAST = ['Dlamini', 'Nkosi', 'Mthembu', 'Zulu', 'Ndlovu', 'Khumalo', 'Mokoena', 'Maseko', 'Mahlangu', 'Masondo', 'van der Merwe', 'Pretorius', 'du Plessis', 'Kruger', 'Botha', 'Steyn', 'Naidoo', 'Pillay', 'Govender', 'Moodley', 'Abrahams', 'Davids', 'October', 'Solomons', 'Jacobs', 'Matsinhe', 'Chirwa', 'Banda', 'Ouma', 'Mwangi', 'Kamau'];

// ---------- Registration plate generator per country/region ----------
const LETTERS = 'ABCDEFGHJKLMNPRSTVWXYZ';
const randLetter = (r, n = 1) => Array.from({ length: n }, () => LETTERS[Math.floor(r() * LETTERS.length)]).join('');
const randDigits = (r, n) => Array.from({ length: n }, () => Math.floor(r() * 10)).join('');

const regoFor = (r, branch) => {
  if (branch.country === 'South Africa') {
    const suffix = { Gauteng: 'GP', 'Western Cape': 'WP', 'KwaZulu-Natal': 'KZN', 'Eastern Cape': 'EC' }[branch.region] || 'ZA';
    return `${randLetter(r, 2)} ${randDigits(r, 2)} ${randLetter(r, 2)} ${suffix}`;
  }
  if (branch.country === 'Namibia')     return `N ${randDigits(r, 5)} W`;
  if (branch.country === 'Botswana')    return `B ${randDigits(r, 3)} ${randLetter(r, 3)}`;
  if (branch.country === 'Mozambique')  return `${randLetter(r, 3)}-${randDigits(r, 2)}-MP`;
  if (branch.country === 'Zimbabwe')    return `A${randLetter(r, 2)} ${randDigits(r, 4)}`;
  if (branch.country === 'Zambia')      return `BA${randLetter(r, 1)} ${randDigits(r, 4)}`;
  if (branch.country === 'Kenya')       return `K${randLetter(r, 2)} ${randDigits(r, 3)}${randLetter(r, 1)}`;
  return `${randLetter(r, 3)} ${randDigits(r, 4)}`;
};

const branchPrefix = (branch) =>
  branch.name.split(/[\s-]+/).filter(Boolean).slice(0, 2).map((w) => w.slice(0, 3).toUpperCase()).join('');

// ---------- Trend generator (12m) ----------
const makeSeries = (r, base, drift = 0.01, noise = 0.08) => {
  const out = [];
  let v = base;
  for (let i = 0; i < 12; i++) {
    v = v * (1 + drift + (r() - 0.5) * noise);
    out.push(+v.toFixed(2));
  }
  return out;
};

// ---------- Status allocation ----------
// Per fleet: ~78% active, ~10% in service, ~7% standby, ~5% grounded.
const STATUS_ROLL = (r) => {
  const x = r();
  if (x < 0.78) return 'Active';
  if (x < 0.88) return 'Service';
  if (x < 0.95) return 'Standby';
  return 'Grounded';
};

// ---------- Build fleet ----------
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const DAY_MS   = 24 * 60 * 60 * 1000;
const now = Date.now();

function buildBranchFleet(branch) {
  const tier = FLEET_TIER[branch.peerGroup] || FLEET_TIER['Metro-Standard'];
  const r = rng(`fleet::${branch.id}`);
  const size = intRange(r, tier[0], tier[1]);
  const prefix = branchPrefix(branch);

  const fleet = [];
  for (let i = 1; i <= size; i++) {
    const vid = `PG-${prefix}-${String(i).padStart(3, '0')}`;
    const vr = rng(`veh::${vid}`);

    // type pick weighted by share
    const typeRoll = vr();
    let acc = 0;
    const type = VEHICLE_TYPES.find((t) => (acc += t.share) >= typeRoll) || VEHICLE_TYPES[0];

    const status = STATUS_ROLL(vr);
    const active = status === 'Active' || status === 'Service';

    // Monthly km scales with branch SLA/jobs — busier branches run further
    const branchBoost = (branch.ops.jobsToday / 38) ** 0.5; // normalised around default 38
    const kmThisMonth = Math.round(range(vr, type.baseKm - type.kmVar, type.baseKm + type.kmVar) * branchBoost * (active ? 1 : 0.25));
    const fuelEff = +range(vr, type.fuelEff - type.fuelEffVar, type.fuelEff + type.fuelEffVar).toFixed(2);
    const fuelCostMTD = Math.round((kmThisMonth * fuelEff / 100) * FUEL_PRICE_ZAR_PER_L);

    const maintCostMTD = Math.round(range(vr, type.baseMaint - type.maintVar, type.baseMaint + type.maintVar) * (active ? 1 : 1.4));
    const stockSalesMTD = type.baseRev === 0 ? 0
      : Math.round(range(vr, type.baseRev - type.revVar, type.baseRev + type.revVar) * branchBoost * (active ? 1 : 0.15));
    const utilPct = Math.max(0, Math.min(99, Math.round(range(vr, type.baseUtil - type.utilVar, type.baseUtil + type.utilVar) * (active ? 1 : 0.3))));

    const ageYears = +range(vr, 0.5, 7.5).toFixed(1);
    const year = new Date().getFullYear() - Math.floor(ageYears);
    const kmLifetime = Math.round(ageYears * type.baseKm * 12 * range(vr, 0.85, 1.15));

    const lastServiceKm = Math.max(0, kmLifetime - Math.round(range(vr, 200, type.servKmInterval)));
    const nextServiceDueKm = lastServiceKm + type.servKmInterval;
    const kmToService = nextServiceDueKm - kmLifetime;

    const licenceExpiry = new Date(now + intRange(vr, -20, 320) * DAY_MS).toISOString().slice(0, 10);
    const insuranceExpiry = new Date(now + intRange(vr, -10, 330) * DAY_MS).toISOString().slice(0, 10);
    const incidents12m = Math.max(0, Math.round(vr() * 4 - 1.5));

    const techAssigned = `${pick(vr, TECH_FIRST)} ${pick(vr, TECH_LAST)}`;
    const model = pick(vr, type.models);

    const costPerKm = kmThisMonth > 0 ? +((fuelCostMTD + maintCostMTD) / kmThisMonth).toFixed(2) : 0;
    const revPerKm = kmThisMonth > 0 ? +(stockSalesMTD / kmThisMonth).toFixed(2) : 0;

    fleet.push({
      id: vid,
      registration: regoFor(vr, branch),
      branchId: branch.id,
      branchName: branch.name,
      region: branch.region,
      country: branch.country,
      typeId: type.id,
      typeName: type.name,
      make: model.split(' ')[0],
      model,
      year,
      ageYears,
      techAssigned,
      status,
      kmThisMonth,
      kmYTD: Math.round(kmThisMonth * range(vr, 3.6, 4.4)),
      kmLifetime,
      fuelLPer100km: fuelEff,
      fuelCostMTD,
      fuelCostYTD: Math.round(fuelCostMTD * range(vr, 3.7, 4.3)),
      maintCostMTD,
      maintCostYTD: Math.round(maintCostMTD * range(vr, 3.8, 4.2)),
      stockSalesMTD,
      stockSalesYTD: Math.round(stockSalesMTD * range(vr, 3.7, 4.3)),
      utilPct,
      idlePct: Math.max(0, 100 - utilPct - intRange(vr, 2, 8)),
      costPerKm,
      revPerKm,
      lastServiceKm,
      nextServiceDueKm,
      kmToService,
      licenceExpiry,
      insuranceExpiry,
      incidents12m,
      trends: {
        km: makeSeries(vr, type.baseKm, 0.004, 0.08),
        fuelCost: makeSeries(vr, type.baseKm * type.fuelEff / 100 * FUEL_PRICE_ZAR_PER_L, 0.006, 0.09),
        maintCost: makeSeries(vr, type.baseMaint, 0.008, 0.16),
        utilPct: makeSeries(vr, type.baseUtil, 0.002, 0.05).map((v) => Math.min(99, Math.max(0, +v.toFixed(1)))),
      },
    });
  }
  return fleet;
}

// ---------- Build once ----------
export const VEHICLES = BRANCHES.flatMap(buildBranchFleet);

// ---------- Queries ----------
export const vehiclesByBranch = (branchId) => VEHICLES.filter((v) => v.branchId === branchId);

// ---------- Rollups ----------
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
const avg = (xs) => (xs.length ? sum(xs) / xs.length : 0);

export function rollupFleet(vehicles) {
  if (!vehicles.length) return null;
  const active   = vehicles.filter((v) => v.status === 'Active').length;
  const service  = vehicles.filter((v) => v.status === 'Service').length;
  const standby  = vehicles.filter((v) => v.status === 'Standby').length;
  const grounded = vehicles.filter((v) => v.status === 'Grounded').length;

  const kmMTD        = sum(vehicles.map((v) => v.kmThisMonth));
  const fuelMTD      = sum(vehicles.map((v) => v.fuelCostMTD));
  const maintMTD     = sum(vehicles.map((v) => v.maintCostMTD));
  const revMTD       = sum(vehicles.map((v) => v.stockSalesMTD));
  const totalCostMTD = fuelMTD + maintMTD;

  return {
    size: vehicles.length,
    active, service, standby, grounded,
    activePct: +(active / vehicles.length * 100).toFixed(1),
    kmMTD, kmYTD: sum(vehicles.map((v) => v.kmYTD)),
    fuelMTD, fuelYTD: sum(vehicles.map((v) => v.fuelCostYTD)),
    maintMTD, maintYTD: sum(vehicles.map((v) => v.maintCostYTD)),
    revMTD, revYTD: sum(vehicles.map((v) => v.stockSalesYTD)),
    totalCostMTD,
    avgUtilPct: +avg(vehicles.map((v) => v.utilPct)).toFixed(1),
    avgFuelEff: +avg(vehicles.map((v) => v.fuelLPer100km)).toFixed(2),
    costPerKm: kmMTD > 0 ? +(totalCostMTD / kmMTD).toFixed(2) : 0,
    revPerKm:  kmMTD > 0 ? +(revMTD / kmMTD).toFixed(2) : 0,
    revPerVehicle: Math.round(revMTD / vehicles.length),
    costPerVehicle: Math.round(totalCostMTD / vehicles.length),
    netPerVehicle: Math.round((revMTD - totalCostMTD) / vehicles.length),
    avgAgeYears: +avg(vehicles.map((v) => v.ageYears)).toFixed(1),
    incidents12m: sum(vehicles.map((v) => v.incidents12m)),
    typeMix: VEHICLE_TYPES.map((t) => ({
      id: t.id,
      name: t.name,
      count: vehicles.filter((v) => v.typeId === t.id).length,
    })),
  };
}

export const FLEET_GROUP  = rollupFleet(VEHICLES);
export const FLEET_BRANCH = Object.fromEntries(BRANCHES.map((b) => [b.id, rollupFleet(vehiclesByBranch(b.id))]));

// ---------- Watchlist / compliance ----------
export function complianceWatchlist(vehicles, daysAhead = 60) {
  const cutoff = now + daysAhead * DAY_MS;
  return vehicles
    .map((v) => {
      const lic = new Date(v.licenceExpiry).getTime();
      const ins = new Date(v.insuranceExpiry).getTime();
      const flags = [];
      if (lic < now) flags.push({ kind: 'licence', tone: 'alert', label: 'LICENCE_EXPIRED' });
      else if (lic < cutoff) flags.push({ kind: 'licence', tone: 'warn', label: `LICENCE_${Math.round((lic - now) / DAY_MS)}D` });
      if (ins < now) flags.push({ kind: 'insurance', tone: 'alert', label: 'INSURANCE_EXPIRED' });
      else if (ins < cutoff) flags.push({ kind: 'insurance', tone: 'warn', label: `INSURANCE_${Math.round((ins - now) / DAY_MS)}D` });
      if (v.kmToService <= 500 && v.kmToService > 0) flags.push({ kind: 'service', tone: 'warn', label: `SERVICE_${v.kmToService}KM` });
      if (v.kmToService <= 0) flags.push({ kind: 'service', tone: 'alert', label: 'SERVICE_OVERDUE' });
      if (v.incidents12m >= 2) flags.push({ kind: 'incidents', tone: 'alert', label: `INCIDENTS_${v.incidents12m}` });
      return { ...v, flags };
    })
    .filter((v) => v.flags.length > 0)
    .sort((a, b) => {
      const aAlert = a.flags.some((f) => f.tone === 'alert');
      const bAlert = b.flags.some((f) => f.tone === 'alert');
      if (aAlert !== bAlert) return aAlert ? -1 : 1;
      return b.flags.length - a.flags.length;
    });
}

// ---------- Leaderboards ----------
export const branchFleetLeaderboard = (metric) =>
  BRANCHES.map((b) => ({
    branch: b,
    fleet: FLEET_BRANCH[b.id],
  })).sort((a, b) => {
    const av = a.fleet[metric]; const bv = b.fleet[metric];
    return (typeof av === 'number' ? bv - av : 0);
  });

// ---------- Formatters ----------
export const fmtR = (v) => 'R ' + Math.round(v).toLocaleString('en-ZA');
export const fmtRk = (v) => {
  if (v >= 1e6) return `R ${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `R ${Math.round(v / 1e3)}k`;
  return `R ${Math.round(v)}`;
};
export const fmtKm = (v) => Math.round(v).toLocaleString('en-ZA') + ' km';

export const MONTHS_FLEET = ['May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr'];
